import { ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import type { FocusEventHandler } from "react";

export function SegmentedControl<Value extends string>(props: {
  label: string;
  options: Array<{ value: Value; label: string }>;
  value: Value;
  onChange: (value: Value) => void;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  disabled?: boolean;
}) {
  const { label, options, value, onChange, onBlur, disabled } = props;
  const theme = useTheme();
  const darkMode = theme.palette.mode === "dark";
  const selectedBackground = darkMode ? "action.selected" : "background.paper";

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      aria-label={label}
      value={value}
      disabled={disabled}
      onBlur={onBlur}
      onChange={(_event, nextValue: Value | null) => {
        if (nextValue !== null) onChange(nextValue);
      }}
      sx={{
        display: "flex",
        width: "100%",
        minWidth: "200px",
        boxSizing: "border-box",
        p: "3px",
        gap: "3px",
        border: 0,
        borderRadius: "999px",
        backgroundColor: darkMode ? "background.default" : "action.selected",
        "& .MuiToggleButtonGroup-grouped": {
          flex: "1 1 0",
          minWidth: 0,
          m: 0,
          border: 0,
          borderRadius: "999px",
          px: 1,
          py: 0.5,
          fontSize: "12px",
          fontWeight: 400,
          lineHeight: 1.5,
          textTransform: "none",
          whiteSpace: "nowrap",
          color: darkMode ? "text.secondary" : "grey.600",
          "&.Mui-selected": {
            backgroundColor: selectedBackground,
            color: darkMode ? "text.primary" : "common.black",
            boxShadow: "none",
            "&:hover": { backgroundColor: selectedBackground },
          },
          "&.Mui-focusVisible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: "1px",
          },
          "&.Mui-disabled": { color: "text.disabled", boxShadow: "none" },
        },
      }}
    >
      {options.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
